import { AppSettings } from '@synergy/admin/app-settings/feature-shell';
import { FeatureFlag, UserPermission, UserRole } from '@synergy/models';

import { AccessControlConnect } from './common/components/access-control/access-control-connect';
import { AccessControlConnectFeature } from './common/components/access-control/access-control-connect-feature';
import { AccessControlConnectInstance } from './common/components/access-control/access-control-connect-instance';
import { AccessControlConnectPermission } from './common/components/access-control/access-control-connect-permission';
import { AccessControlConnectRole } from './common/components/access-control/access-control-connect-role';
import { BonusSettlementCustomerDetail } from './features/bonus-settlements/customer-detail/bonus-settlement-customer-detail';
import { BonusSettlementDetail } from './features/bonus-settlements/detail/bonus-settlement-detail';
import { BonusSettlementSupplierDetail } from './features/bonus-settlements/supplier-detail/bonus-settlement-supplier-detail';
import { FooterSettings } from './features/content-management/footer-settings/footer-settings';
import { HeaderBannerDetail } from './features/content-management/header-banner/header-banner-detail/header-banner-detail';
import { ImportDetail } from './features/products/import/containers/detail/import-detail';
import { ImportOverview } from './features/products/import/containers/import-overview';
import { MappingOverview } from './features/products/mapping/mapping-overview';
import { NewsPostDetail } from './features/content-management/news-post/news-post-detail/news-post-detail';
import { PaloBonusSettlementOverview } from './features/bonus-settlements/palo/palo-bonus-settlement-overview';
import { PaloConfigurationDataOverview } from './features/configuration/data/palo/palo-configuration-data-overview';
import { PaloConfigurationFinanceOverview } from './features/configuration/finance/palo/palo-configuration-finance-overview';
import { PaloConfigurationOrganizationOverview } from './features/configuration/organization/palo/palo-configuration-organization-overview';
import { PaloContactDetail } from './features/contacts-and-organizations/contact/palo/palo-contact-detail';
import { PaloContactList } from './features/contacts-and-organizations/contact/palo/palo-contact-list';
import { PaloCustomerDetail } from './features/contacts-and-organizations/customer/palo/palo-customer-detail';
import { PaloCustomerList } from './features/contacts-and-organizations/customer/palo/palo-customer-list';
import { PaloDashboardOverview } from './features/dashboard/palo/palo-dashboard-overview';
import { PaloDemandDetail } from './features/demands/containers/detail/palo/palo-demand-detail';
import { PaloDemandOverview } from './features/demands/palo/palo-demand-overview';
import { PaloHeaderBannerList } from './features/content-management/header-banner/palo/palo-header-banner-list';
import { PaloInquiryDetail } from './features/inquiries/palo/palo-inquiry-detail';
import { PaloInquiryOverview } from './features/inquiries/palo/palo-inquiry-overview';
import { PaloInvoiceDetail } from './features/invoices/invoice-detail/palo/palo-invoice-detail';
import { PaloInvoiceOverview } from './features/invoices/palo/palo-invoice-overview';
import { PaloNewsPostList } from './features/content-management/news-post/palo/palo-news-post-list';
import { PaloProductDetail } from './features/products/product/palo/palo-product-detail';
import { PaloProductList } from './features/products/product/palo/palo-product-list';
import { PaloPurchaseOrderDetail } from './features/purchase-orders/palo/palo-purchase-order-detail';
import { PaloPurchaseOrderOverview } from './features/purchase-orders/palo/palo-purchase-order-overview';
import { PaloSalesOrderDetail } from './features/sales-orders/palo/palo-sales-order-detail';
import { PaloSalesOrderOverview } from './features/sales-orders/palo/palo-sales-order-overview';
import { PaloSearchConsole } from './features/configuration/search/palo/palo-search-console';
import { PaloSponsorBannerList } from './features/content-management/sponsor-banner/palo/palo-sponsor-banner-list';
import { PaloStaticSitesList } from './features/content-management/static-sites/palo/palo-static-sites-list';
import { PaloSupplierDetail } from './features/contacts-and-organizations/supplier/palo/palo-supplier-detail';
import { PaloSupplierList } from './features/contacts-and-organizations/supplier/palo/palo-supplier-list';
import { PaloSupplierSpecialOffersList } from './features/content-management/supplier-special-offers/palo/palo-supplier-special-offers-list';
import { SponsorBannerDetail } from './features/content-management/sponsor-banner/sponsor-banner-detail/sponsor-banner-detail';
import { StaticSitesDetail } from './features/content-management/static-sites/static-sites-detail/static-sites-detail';
import { SupplierSpecialOffersDetail } from './features/content-management/supplier-special-offers/supplier-special-offers-detail/supplier-special-offers-detail';

const Dashboard = AccessControlConnectPermission(UserPermission.DashBoard, true)(PaloDashboardOverview);
const Demands = AccessControlConnectPermission([UserPermission.Demands], true)(PaloDemandOverview);
const DemandDetails = AccessControlConnectPermission([UserPermission.Demands], true)(PaloDemandDetail);
const Inquiries = AccessControlConnectPermission([UserPermission.Inquiries], true)(PaloInquiryOverview);
const InquiryDetails = AccessControlConnectPermission([UserPermission.Inquiries], true)(PaloInquiryDetail);
const PurchaseOrders = AccessControlConnectPermission([UserPermission.PurchaseOrders], true)(PaloPurchaseOrderOverview);
const PurchaseOrderDetails = AccessControlConnectPermission(
    [UserPermission.PurchaseOrders],
    true
)(PaloPurchaseOrderDetail);
const SalesOrders = AccessControlConnectPermission([UserPermission.SalesOrders], true)(PaloSalesOrderOverview);
const SalesOrderDetails = AccessControlConnectPermission([UserPermission.SalesOrders], true)(PaloSalesOrderDetail);
const Invoices = AccessControlConnectRole(
    [UserRole.Admin, UserRole.SuperAdmin, UserRole.Accounting],
    true
)(PaloInvoiceOverview);
const InvoiceDetails = AccessControlConnectRole(
    [UserRole.Admin, UserRole.SuperAdmin, UserRole.Accounting],
    true
)(PaloInvoiceDetail);
const Customers = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloCustomerList);
const Suppliers = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloSupplierList);
const SupplierDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloSupplierDetail);
const CustomerDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloCustomerDetail);
const ContactDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloContactDetail);
const Contacts = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloContactList);
const ConfigurationData = AccessControlConnectRole([UserRole.Configuration], true)(PaloConfigurationDataOverview);
const ConfigurationSearch = AccessControlConnectRole([UserRole.Configuration], true)(PaloSearchConsole);
const ConfigurationOrganization = AccessControlConnectRole(
    [UserRole.Configuration],
    true
)(PaloConfigurationOrganizationOverview);
const ConfigurationFinance = AccessControlConnectRole([UserRole.Configuration], true)(PaloConfigurationFinanceOverview);
const Mappings = AccessControlConnectRole([UserRole.Configuration], true)(MappingOverview);
const Imports = AccessControlConnectRole([UserRole.Configuration], true)(ImportOverview);
const ImportDetails = AccessControlConnectRole([UserRole.Configuration], true)(ImportDetail);
const Products = AccessControlConnect([UserRole.Configuration], undefined, undefined, undefined, true)(PaloProductList);
const ProductDetails = AccessControlConnect(
    [UserRole.Configuration],
    undefined,
    undefined,
    undefined,
    true
)(PaloProductDetail);
const HeaderBanner = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloHeaderBannerList);
const HeaderBannerDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(HeaderBannerDetail);
const NewsPost = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloNewsPostList);
const NewsPostDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(NewsPostDetail);
const StaticSites = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloStaticSitesList);
const StaticSitesDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(StaticSitesDetail);
const FooterSetting = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(FooterSettings);
const SupplierSpecialOffers = AccessControlConnectRole(
    [UserRole.Admin, UserRole.SuperAdmin],
    true
)(PaloSupplierSpecialOffersList);
const SupplierSpecialOfferDetails = AccessControlConnectRole(
    [UserRole.Admin, UserRole.SuperAdmin],
    true
)(SupplierSpecialOffersDetail);
const SponsorBanner = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(PaloSponsorBannerList);
const SponsorBannerDetails = AccessControlConnectRole([UserRole.Admin, UserRole.SuperAdmin], true)(SponsorBannerDetail);
const RestrictedAppSettings = AccessControlConnectRole([UserRole.SuperAdmin], true)(AppSettings);
const BonusSettlementsRestricted = AccessControlConnectFeature(
    FeatureFlag.BonusSettlements,
    true
)(PaloBonusSettlementOverview);
const BonusSettlementOverviewRestricted = AccessControlConnectFeature(
    FeatureFlag.BonusSettlements,
    true
)(BonusSettlementDetail);
const BonusSettlementSupplierDetailRestricted = AccessControlConnectFeature(
    FeatureFlag.BonusSettlements,
    true
)(BonusSettlementSupplierDetail);
const BonusSettlementCustomerDetailRestricted = AccessControlConnectFeature(
    FeatureFlag.BonusSettlements,
    true
)(BonusSettlementCustomerDetail);

export const protectedComponents = {
    Dashboard,
    Demands,
    DemandDetails,
    Inquiries,
    InquiryDetails,
    PurchaseOrders,
    PurchaseOrderDetails,
    SalesOrders,
    SalesOrderDetails,
    Invoices,
    InvoiceDetails,
    Customers,
    Suppliers,
    SupplierDetails,
    CustomerDetails,
    ContactDetails,
    Contacts,
    ConfigurationData,
    ConfigurationSearch,
    ConfigurationOrganization,
    ConfigurationFinance,
    Mappings,
    Imports,
    ImportDetails,
    Products,
    ProductDetails,
    HeaderBanner,
    HeaderBannerDetails,
    NewsPost,
    NewsPostDetails,
    StaticSites,
    StaticSitesDetails,
    FooterSetting,
    SupplierSpecialOffers,
    SupplierSpecialOfferDetails,
    SponsorBanner,
    SponsorBannerDetails,
    RestrictedAppSettings,
    BonusSettlementsRestricted,
    BonusSettlementOverviewRestricted,
    BonusSettlementSupplierDetailRestricted,
    BonusSettlementCustomerDetailRestricted,
};
